import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import * as Sentry from '@sentry/browser';
import qpqLanguageFile from 'scaleflow/custom-translations/en-qpq'; // Adjust path if needed
import { HOMEPAGE } from 'scaleflow/controllers/application';
import type IntlService from 'ember-intl/services/intl';
import type CurrentUserService from 'scaleflow/services/current-user';
import type SessionService from 'scaleflow/services/session';
import type StoreService from 'scaleflow/services/store';
import type TrackerService from 'scaleflow/services/tracker';
import nameSort from 'scaleflow/utils/nameSort';
import { appendSwitchTenantParam } from 'scaleflow/utils/switch-tenant-url';

export interface ConfidenceOption {
  label: string;
  value: number;
}

/** The slice of the `organisation` record this service reads. */
interface OrganisationRecord {
  id: string;
  name?: string;
  locale?: string;
  first_day_of_week?: number;
  bon_price_person_week?: number;
  bon_horizon?: number;
  bon_clv?: number;
  customer_values?: Record<string, number>;
  bon_investment_confidences?: Record<string, number>;
  bon_return_confidences?: Record<string, number>;
  bucket_size_vision?: number;
  bucket_size_customer_request?: number;
  bucket_size_operations?: number;
  unavailable_time?: unknown;
  metadata?: { plan?: string; inviting_enabled?: boolean };
  get(key: string): unknown;
}

export default class OrganisationService extends Service {
  @service declare session: SessionService;
  @service declare store: StoreService;
  @service('current-user') declare currentUser: CurrentUserService;
  @tracked _organisation: OrganisationRecord | null | undefined = null;
  @tracked organisationList: OrganisationRecord[] = [];
  @service declare tracker: TrackerService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  acceptedLocales = ['en-US', 'nl-NL', 'en-GB', 'en-qpq'];

  defaultInvestmentConfidenceOptions: ConfidenceOption[] = [
    {
      label: 'High',
      value: 1.25,
    },
    {
      label: 'Medium',
      value: 1.5,
    },
    {
      label: 'Low',
      value: 3,
    },
  ];

  defaultReturnConfidenceOptions: ConfidenceOption[] = [
    {
      label: 'High',
      value: 0.75,
    },
    {
      label: 'Medium',
      value: 0.5,
    },
    {
      label: 'Low',
      value: 0.25,
    },
  ];

  set organisation(value: OrganisationRecord | undefined) {
    this._organisation = value;
    const tenantId: string | undefined = this.session?.activeTenantId;
    Sentry.setTag('tenant_id', tenantId);
    this.tracker.setTenant(tenantId, {
      name: value?.name,
      id: tenantId,
    });
  }

  get organisationAvatar() {
    const title = this._organisation?.get('name') as string | undefined;
    if (title) {
      return title[0]!.toUpperCase();
    }
    return '';
  }

  get organisation(): OrganisationRecord | null | undefined {
    return this._organisation;
  }

  get name() {
    return this._organisation?.name;
  }

  get sortedOrganisationList() {
    return [...this.organisationList].sort((a, b) => nameSort(a.name, b.name));
  }

  @action
  isCurrentOrganisation(organisation_id: string) {
    return this.organisation?.id === organisation_id;
  }

  get locale() {
    if (!this._organisation?.locale) return 'nl-NL';
    if (this.acceptedLocales.includes(this._organisation?.locale)) {
      return this._organisation?.locale;
    }
    return 'nl-NL';
  }

  get id() {
    return this.organisation?.id;
  }

  get first_day_of_week() {
    return this._organisation?.first_day_of_week;
  }

  get roadmap_url() {
    return this._organisation?.get('roadmap_url');
  }

  get bookmarks() {
    return this._organisation?.get('bookmarks');
  }

  get ppw() {
    return this._organisation?.bon_price_person_week ?? 1700;
  }

  get horizon() {
    return this._organisation?.bon_horizon ?? 12;
  }

  get clv_in_horizon() {
    return this._organisation?.bon_clv ?? 80_000;
  }

  get customer_values() {
    return this._organisation!.customer_values ?? {};
  }

  get customer_values_options() {
    return Object.entries(this.customer_values).map(([key, value]) => {
      return {
        label: this.intl.t(`financials.customer-values.${key.toLowerCase()}`),
        value: value,
        key: key,
      };
    });
  }

  get is_trial() {
    return this._organisation?.metadata?.plan === 'trial';
  }

  get inviting_is_disabled() {
    return this._organisation?.metadata?.inviting_enabled == false;
  }

  get bon_investment_confidences() {
    return (
      this._organisation?.bon_investment_confidences ??
      this.defaultInvestmentConfidenceOptions.reduce(
        (acc, item) => {
          acc[item.label.toLowerCase()] = item.value;
          return acc;
        },
        {} as Record<string, number>,
      )
    );
  }

  get bon_return_confidences() {
    return (
      this._organisation?.bon_return_confidences ??
      this.defaultReturnConfidenceOptions.reduce(
        (acc, item) => {
          acc[item.label.toLowerCase()] = item.value;
          return acc;
        },
        {} as Record<string, number>,
      )
    );
  }

  get investmentConfidenceOptions() {
    const confidenceObject = this._organisation?.bon_investment_confidences;
    if (confidenceObject) {
      return [
        ...Object.keys(confidenceObject).map((key) => {
          return {
            value: +confidenceObject[key]!,
            label: key.charAt(0).toUpperCase() + key.slice(1),
          };
        }),
        { value: 1, label: 'Confirmed' },
      ].reverse();
    }
    return this.defaultInvestmentConfidenceOptions;
  }

  get returnConfidenceOptions() {
    const confidenceObject = this._organisation?.bon_return_confidences;
    if (confidenceObject) {
      return [
        ...Object.keys(confidenceObject).map((key) => {
          return {
            value: +confidenceObject[key]!,
            label: key.charAt(0).toUpperCase() + key.slice(1),
          };
        }),
        { value: 1, label: 'Confirmed' },
      ].reverse();
    }
    return this.defaultReturnConfidenceOptions;
  }

  async load() {
    const tenant_id: string | undefined = this.session?.activeTenantId;
    if (!tenant_id) {
      console.debug('Could not determine organisation');
      return;
    }
    // Commit this tab to its tenant once, at boot -- so a later background
    // refresh (which never touches sessionStorage) can't move it out from
    // under an already-running tab.
    if (!window.sessionStorage.getItem('tenant')) {
      window.sessionStorage.setItem('tenant', tenant_id);
    }
    this.organisationList = (await this.store.findAll(
      'organisation',
    )) as unknown as OrganisationRecord[];
    this.organisation = (await this.store.findRecord(
      'organisation',
      tenant_id,
    )) as OrganisationRecord;
    const locale = this.organisation?.locale ?? '';
    if (this.acceptedLocales.includes(locale)) {
      if (locale === 'en-qpq') {
        this.intl.addTranslations('en-US', qpqLanguageFile);
        this.intl.setLocale('en-US');
      } else this.intl.setLocale(locale);
    } else this.intl.setLocale('nl-NL');
  }

  getInvestmentConfidenceLabel(confidence: number) {
    const option = this.investmentConfidenceOptions.find(
      (option) => option.value == confidence,
    );
    return option?.label;
  }

  getReturnConfidenceLabel(confidence: number) {
    const option = this.investmentConfidenceOptions.find(
      (option) => option.value == confidence,
    );
    return option?.label;
  }

  get vision_capacity() {
    return this?._organisation?.bucket_size_vision;
  }

  get customer_request_capacity() {
    return this?._organisation?.bucket_size_customer_request;
  }

  get operations_capacity() {
    return this?._organisation?.bucket_size_operations;
  }

  get unavailable_time() {
    return this._organisation?.unavailable_time;
  }

  // A dynamic segment in the URL is a record id belonging to the org being left,
  // so reloading in place would just refetch it under the new tenant and 404.
  // Walking up the chain matters: `.../teams/:team/check-in/archive/questions`
  // owns its id four levels above the leaf.
  get currentRouteIsRecordScoped() {
    let routeInfo = this.router.currentRoute;
    while (routeInfo) {
      if (routeInfo.paramNames?.length) return true;
      routeInfo = routeInfo.parent;
    }
    return false;
  }

  // The href a dropdown link hands to the browser so a ctrl/cmd/middle-click
  // opens a new tab on this tenant: `initializers/switch-tenant.js` reads the
  // param before the router's first transition and commits it to that tab's
  // own `sessionStorage`, then strips it from the url.
  urlForSwitch(tenant_id: string) {
    const base = this.currentRouteIsRecordScoped
      ? this.router.urlFor(HOMEPAGE)
      : this.router.currentURL;
    return appendSwitchTenantParam(base, tenant_id);
  }

  @action
  switchOrganisation(tenant_id: string) {
    if (!tenant_id) throw new Error('No Organisation found to switch to');
    window.localStorage.setItem('tenant', tenant_id);
    window.sessionStorage.setItem('tenant', tenant_id);
    this.tracker.reset();
    // Switching tenant needs a full boot either way — the difference is only
    // whether the URL survives it. Org-agnostic routes (lists, dashboards) keep
    // the user where they were; record URLs are dead and go to the homepage.
    if (this.currentRouteIsRecordScoped) {
      this.navigateToUrl(this.router.urlFor(HOMEPAGE));
      return;
    }
    this.reloadPage();
  }

  // Wrapped so tests can observe the intent: letting a real navigation run
  // would tear down the test runner's page mid-suite.
  reloadPage() {
    window.location.reload();
  }

  navigateToUrl(url: string) {
    window.location.href = url;
  }

  clear() {
    this.organisation = undefined;
  }
}
